import React, {
    useEffect,
    useState,
} from "react";

import {
    Flex,
    Box,
    Text,
} from "@chakra-ui/react";

import {
    EllipsisVertical,
    Pencil,
    EyeIcon,
    Trash2,
    List,
} from "lucide-react";

import ReusableTable from "components/Tables/ReusableTable";

import ThreeDotMenu from "components/Menus/ThreeDotMenu";

import GradientButton from "components/Buttons/GradientButton";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import StatusBadge from "components/Badges/StatusBadge";

import {
    fetchAssessmentsAction,
    createAssessmentAction,
    updateAssessmentAction,
    deleteAssessmentAction,
} from "state/actions/assessmentActions";

import {
    fetchQuestionsAction,
    createQuestionAction,
    updateQuestionAction,
    deleteQuestionAction,
} from "state/actions/questionActions";

import {
    useCustomNotification,
} from "components/Toasts/OpenNotification";

import AddAssessmentDrawer from "./components/AddAssessmentDrawer";

import ViewAssessmentDrawer from "./components/ViewAssessmentDrawer";

import EditAssessmentDrawer from "./components/EditAssessmentDrawer";

import DeleteAssessment from "./components/DeleteAssessment";

import QuestionsDrawer from "./components/QuestionsDrawer";

import AddQuestionDrawer from "./components/AddQuestionDrawer";

import ViewQuestionDrawer from "./components/ViewQuestionDrawer";

import EditQuestionDrawer from "./components/EditQuestionDrawer";

import DeleteQuestion from "./components/DeleteQuestion";


const AgentAssessmentManagement = () => {

    const dispatch =
        useDispatch();

    const notify =
        useCustomNotification();


    
    // REDUX
    

    const {
        assessments = [],
        loading: assessmentLoading,
        error: assessmentError,
    } = useSelector(
        (state) =>
            state.assessments || {}
    );


    const {
        questions = [],
        loading: questionLoading,
        error: questionError,
    } = useSelector(
        (state) =>
            state.questions || {}
    );


    
    // ASSESSMENT DRAWERS
    

    const [
        isAddAssessmentOpen,
        setAddAssessmentOpen,
    ] = useState(false);


    const [
        isViewAssessmentOpen,
        setViewAssessmentOpen,
    ] = useState(false);


    const [
        isEditAssessmentOpen,
        setEditAssessmentOpen,
    ] = useState(false);


    
    // QUESTION DRAWERS
    

    const [
        isQuestionsOpen,
        setQuestionsOpen,
    ] = useState(false);


    const [
        isAddQuestionOpen,
        setAddQuestionOpen,
    ] = useState(false);


    const [
        isViewQuestionOpen,
        setViewQuestionOpen,
    ] = useState(false);


    const [
        isEditQuestionOpen,
        setEditQuestionOpen,
    ] = useState(false);


    
    // DELETE MODALS
    

    const [
        isDeleteAssessmentOpen,
        setDeleteAssessmentOpen,
    ] = useState(false);


    const [
        isDeleteQuestionOpen,
        setDeleteQuestionOpen,
    ] = useState(false);


    
    // SELECTED
    

    const [
        selectedAssessment,
        setSelectedAssessment,
    ] = useState(null);


    const [
        selectedQuestion,
        setSelectedQuestion,
    ] = useState(null);


    
    // FETCH ASSESSMENTS
    

    useEffect(() => {

        dispatch(
            fetchAssessmentsAction()
        );

    }, [dispatch]);


    
    // ASSESSMENT HANDLERS
    

    const handleViewAssessment = (
        assessment
    ) => {

        setSelectedAssessment(
            assessment
        );

        setViewAssessmentOpen(
            true
        );
    };


    const handleCloseViewAssessment = () => {

        setSelectedAssessment(
            null
        );

        setViewAssessmentOpen(
            false
        );
    };


    const handleEditAssessment = (
        assessment
    ) => {

        setSelectedAssessment(
            assessment
        );

        setEditAssessmentOpen(
            true
        );
    };


    const handleCloseEditAssessment = () => {

        setSelectedAssessment(
            null
        );

        setEditAssessmentOpen(
            false
        );
    };


    
    // ADD ASSESSMENT
    

    const handleAddAssessment = async (
        payload
    ) => {

        try {

            const response =
                await dispatch(
                    createAssessmentAction(
                        payload
                    )
                );


            if (
                response?.success
            ) {

                notify({
                    message:
                        "Assessment Created",
                    description:
                        "Certification assessment created successfully.",
                    type:
                        "success",
                });


                setAddAssessmentOpen(
                    false
                );


                dispatch(
                    fetchAssessmentsAction()
                );

            } else {

                notify({
                    message:
                        "Failed to Create Assessment",
                    description:
                        response?.error ||
                        "Unable to create assessment.",
                    type:
                        "error",
                });

            }


            return response;

        } catch (error) {

            notify({
                message:
                    "Error Creating Assessment",
                description:
                    error.message ||
                    "Something went wrong.",
                type:
                    "error",
            });


            return {
                success:
                    false,

                error:
                    error.message,
            };
        }
    };


    
    // UPDATE ASSESSMENT
    

    const handleUpdateAssessment = async (
        assessmentId,
        payload
    ) => {

        try {

            const response =
                await dispatch(
                    updateAssessmentAction(
                        assessmentId,
                        payload
                    )
                );


            if (
                response?.success
            ) {

                notify({
                    message:
                        "Assessment Updated",
                    description:
                        "Assessment updated successfully.",
                    type:
                        "success",
                });


                setEditAssessmentOpen(
                    false
                );


                setSelectedAssessment(
                    null
                );


                dispatch(
                    fetchAssessmentsAction()
                );

            } else {

                notify({
                    message:
                        "Failed to Update Assessment",
                    description:
                        response?.error ||
                        "Unable to update assessment.",
                    type:
                        "error",
                });

            }


            return response;

        } catch (error) {

            notify({
                message:
                    "Error Updating Assessment",
                description:
                    error.message ||
                    "Something went wrong.",
                type:
                    "error",
            });


            return {
                success:
                    false,

                error:
                    error.message,
            };
        }
    };


    
    // DELETE ASSESSMENT
    

    const handleDeleteAssessment = async () => {

        if (
            !selectedAssessment
                ?.assessment_id
        ) {
            return;
        }


        try {

            const response =
                await dispatch(
                    deleteAssessmentAction(
                        selectedAssessment
                            .assessment_id
                    )
                );


            if (
                response?.success
            ) {

                notify({
                    message:
                        "Assessment Deleted",
                    description:
                        "Assessment deleted successfully.",
                    type:
                        "success",
                });


                setDeleteAssessmentOpen(
                    false
                );


                setSelectedAssessment(
                    null
                );


                dispatch(
                    fetchAssessmentsAction()
                );

            } else {

                notify({
                    message:
                        "Failed to Delete Assessment",
                    description:
                        response?.error ||
                        "Unable to delete assessment.",
                    type:
                        "error",
                });

            }

        } catch (error) {

            notify({
                message:
                    "Error Deleting Assessment",
                description:
                    error.message ||
                    "Something went wrong.",
                type:
                    "error",
            });
        }
    };


    
    // OPEN QUESTIONS
    

    const handleOpenQuestions = (
        assessment
    ) => {

        setSelectedAssessment(
            assessment
        );


        setQuestionsOpen(
            true
        );


        dispatch(
            fetchQuestionsAction(
                assessment.assessment_id
            )
        );
    };


    const handleCloseQuestions = () => {

        setQuestionsOpen(
            false
        );

        setSelectedAssessment(
            null
        );

        setSelectedQuestion(
            null
        );
    };


    
    // ADD QUESTION
    

    const handleAddQuestion = async (
        payload
    ) => {

        try {

            const response =
                await dispatch(
                    createQuestionAction(
                        payload
                    )
                );


            if (
                response?.success
            ) {

                notify({
                    message:
                        "Question Created",
                    description:
                        "Question added successfully.",
                    type:
                        "success",
                });


                setAddQuestionOpen(
                    false
                );


                dispatch(
                    fetchQuestionsAction(
                        selectedAssessment
                            ?.assessment_id
                    )
                );

            } else {

                notify({
                    message:
                        "Failed to Create Question",
                    description:
                        response?.error ||
                        "Unable to create question.",
                    type:
                        "error",
                });

            }


            return response;

        } catch (error) {

            notify({
                message:
                    "Error Creating Question",
                description:
                    error.message ||
                    "Something went wrong.",
                type:
                    "error",
            });


            return {
                success:
                    false,

                error:
                    error.message,
            };
        }
    };


    
    // VIEW QUESTION
    

    const handleViewQuestion = (
        question
    ) => {

        setSelectedQuestion(
            question
        );

        setViewQuestionOpen(
            true
        );
    };


    const handleCloseViewQuestion = () => {

        setSelectedQuestion(
            null
        );

        setViewQuestionOpen(
            false
        );
    };


    
    // EDIT QUESTION
    

    const handleEditQuestion = (
        question
    ) => {

        setSelectedQuestion(
            question
        );

        setEditQuestionOpen(
            true
        );
    };


    const handleCloseEditQuestion = () => {

        setSelectedQuestion(
            null
        );

        setEditQuestionOpen(
            false
        );
    };


    const handleUpdateQuestion = async (
        questionId,
        payload
    ) => {

        try {

            const response =
                await dispatch(
                    updateQuestionAction(
                        questionId,
                        payload
                    )
                );


            if (
                response?.success
            ) {

                notify({
                    message:
                        "Question Updated",
                    description:
                        "Question updated successfully.",
                    type:
                        "success",
                });


                setEditQuestionOpen(
                    false
                );


                setSelectedQuestion(
                    null
                );


                dispatch(
                    fetchQuestionsAction(
                        selectedAssessment
                            ?.assessment_id
                    )
                );

            } else {

                notify({
                    message:
                        "Failed to Update Question",
                    description:
                        response?.error ||
                        "Unable to update question.",
                    type:
                        "error",
                });

            }


            return response;

        } catch (error) {

            notify({
                message:
                    "Error Updating Question",
                description:
                    error.message ||
                    "Something went wrong.",
                type:
                    "error",
            });


            return {
                success:
                    false,

                error:
                    error.message,
            };
        }
    };


    
    // DELETE QUESTION
    

    const handleDeleteQuestion = async () => {

        if (
            !selectedQuestion
                ?.question_id
        ) {
            return;
        }


        try {

            const response =
                await dispatch(
                    deleteQuestionAction(
                        selectedQuestion
                            .question_id
                    )
                );


            if (
                response?.success
            ) {

                notify({
                    message:
                        "Question Deleted",
                    description:
                        "Question deleted successfully.",
                    type:
                        "success",
                });


                setDeleteQuestionOpen(
                    false
                );


                setSelectedQuestion(
                    null
                );


                dispatch(
                    fetchQuestionsAction(
                        selectedAssessment
                            ?.assessment_id
                    )
                );

            } else {

                notify({
                    message:
                        "Failed to Delete Question",
                    description:
                        response?.error ||
                        "Unable to delete question.",
                    type:
                        "error",
                });

            }

        } catch (error) {

            notify({
                message:
                    "Error Deleting Question",
                description:
                    error.message ||
                    "Something went wrong.",
                type:
                    "error",
            });
        }
    };


    
    // ASSESSMENT ACTIONS
    

    const generateAssessmentMenuItems = (
        row
    ) => [

        {
            icon: (
                <EyeIcon
                    size={16}
                />
            ),

            text:
                "View Assessment",

            onClick: () =>
                handleViewAssessment(
                    row.original
                ),
        },


        {
            icon: (
                <Pencil
                    size={16}
                />
            ),

            text:
                "Edit Assessment",

            onClick: () =>
                handleEditAssessment(
                    row.original
                ),
        },


        {
            icon: (
                <List
                    size={16}
                />
            ),

            text:
                "Manage Questions",

            onClick: () =>
                handleOpenQuestions(
                    row.original
                ),
        },


        {
            icon: (
                <Trash2
                    size={16}
                />
            ),

            text:
                "Delete Assessment",

            onClick: () => {

                setSelectedAssessment(
                    row.original
                );

                setDeleteAssessmentOpen(
                    true
                );

            },
        },

    ];


    
    // TABLE COLUMNS
    

    const columnsData = [

        {
            Header:
                "Assessment",

            accessor:
                "title",

            Cell: ({
                value,
            }) => (

                <Box>

                    <Text
                        fontWeight="600"
                    >
                        {
                            value ||
                            "N/A"
                        }
                    </Text>

                </Box>
            ),
        },

        {
            Header:
                "Pass Mark",

            accessor:
                "pass_mark",

            Cell: ({
                value,
            }) => (

                <Text
                    fontWeight="600"
                >
                    {value ?? 0}%
                </Text>
            ),
        },


        {
            Header:
                "Status",

            accessor:
                "active",

            Cell: ({
                value,
            }) => (

                <StatusBadge

                    label={
                        value
                            ? "Active"
                            : "Inactive"
                    }

                    variant={
                        value
                            ? "success"
                            : "gray"
                    }

                />
            ),
        },


        {
            Header:
                "Created",

            accessor:
                "created_at",

            Cell: ({
                value,
            }) => (

                <Text
                    fontSize="sm"
                >
                    {
                        value
                            ? new Date(
                                value
                            ).toLocaleDateString()
                            : "N/A"
                    }
                </Text>
            ),
        },


        // ==================================
        // ACTIONS
        // ==================================

        {
            Header:
                "Actions",

            accessor:
                "moreOptions",

            Cell: ({
                row,
            }) => (

                <ThreeDotMenu

                    icon={
                        <EllipsisVertical
                            size={20}
                            color="grey"
                        />
                    }

                    items={
                        generateAssessmentMenuItems(
                            row
                        )
                    }

                />

            ),
        },

    ];


    
    // SAFE TABLE DATA
    

    const tableData =
        Array.isArray(
            assessments
        )
            ? assessments
            : Array.isArray(
                assessments?.data
            )
                ? assessments.data
                : [];


    
    // RENDER
    

    return (

        <Flex
            flexDirection="column"
            pt={{
                base: "120px",
                md: "25px",
            }}
        >

            {/* HEADER */}

            <Box p={6}>

                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >

                    <Text
                        fontSize="md"
                        fontWeight="bold"
                    >

                        {assessmentLoading ? (

                            <Skeleton
                                height="20px"
                                width="150px"
                            />

                        ) : (

                            `All Assessments: ${tableData.length}`

                        )}

                    </Text>


                    <GradientButton
                        height="40px"
                        onClick={() =>
                            setAddAssessmentOpen(
                                true
                            )
                        }
                    >
                        + Add Assessment
                    </GradientButton>

                </Flex>

            </Box>


            {/* TABLE */}

            <ReusableTable

                columnsData={
                    columnsData
                }

                tableData={
                    tableData
                }

                isLoading={
                    assessmentLoading
                }

                error={
                    assessmentError
                }

                emptyMessage={
                    "No certification assessments found."
                }

            />


         
            {/* ADD ASSESSMENT */}
         

            <AddAssessmentDrawer

                isOpen={
                    isAddAssessmentOpen
                }

                onClose={() =>
                    setAddAssessmentOpen(
                        false
                    )
                }

                onSubmit={
                    handleAddAssessment
                }

            />


         
            {/* VIEW ASSESSMENT */}
         

            <ViewAssessmentDrawer

                isOpen={
                    isViewAssessmentOpen
                }

                onClose={
                    handleCloseViewAssessment
                }

                assessment={
                    selectedAssessment
                }

            />


         
            {/* EDIT ASSESSMENT */}
         

            <EditAssessmentDrawer

                isOpen={
                    isEditAssessmentOpen
                }

                onClose={
                    handleCloseEditAssessment
                }

                assessment={
                    selectedAssessment
                }

                onSubmit={
                    handleUpdateAssessment
                }

            />


         
            {/* QUESTIONS */}
         

            <QuestionsDrawer

                isOpen={
                    isQuestionsOpen
                }

                onClose={
                    handleCloseQuestions
                }

                assessment={
                    selectedAssessment
                }

                questions={
                    questions
                }

                loading={
                    questionLoading
                }

                onAddQuestion={() =>
                    setAddQuestionOpen(
                        true
                    )
                }

                onViewQuestion={
                    handleViewQuestion
                }

                onEditQuestion={
                    handleEditQuestion
                }

                onDeleteQuestion={
                    (question) => {

                        setSelectedQuestion(
                            question
                        );

                        setDeleteQuestionOpen(
                            true
                        );

                    }
                }

            />


         
            {/* ADD QUESTION */}
         

            <AddQuestionDrawer

                isOpen={
                    isAddQuestionOpen
                }

                onClose={() =>
                    setAddQuestionOpen(
                        false
                    )
                }

                assessmentId={
                    selectedAssessment
                        ?.assessment_id
                }

                onSubmit={
                    handleAddQuestion
                }

            />


         
            {/* VIEW QUESTION */}
         

            <ViewQuestionDrawer

                isOpen={
                    isViewQuestionOpen
                }

                onClose={
                    handleCloseViewQuestion
                }

                question={
                    selectedQuestion
                }

            />


         
            {/* EDIT QUESTION */}
         

            <EditQuestionDrawer

                isOpen={
                    isEditQuestionOpen
                }

                onClose={
                    handleCloseEditQuestion
                }

                question={
                    selectedQuestion
                }

                onSubmit={
                    handleUpdateQuestion
                }

            />


         
            {/* DELETE ASSESSMENT */}
         

            <DeleteAssessment

                isOpen={
                    isDeleteAssessmentOpen
                }

                onClose={() => {

                    setDeleteAssessmentOpen(
                        false
                    );

                    setSelectedAssessment(
                        null
                    );

                }}

                onConfirm={
                    handleDeleteAssessment
                }

                assessmentTitle={
                    selectedAssessment?.title
                }

            />


         
            {/* DELETE QUESTION */}
         

            <DeleteQuestion

                isOpen={
                    isDeleteQuestionOpen
                }

                onClose={() => {

                    setDeleteQuestionOpen(
                        false
                    );

                    setSelectedQuestion(
                        null
                    );

                }}

                onConfirm={
                    handleDeleteQuestion
                }

                question={
                    selectedQuestion?.question
                }

            />

        </Flex>
    );
};


export default AgentAssessmentManagement;